import { useDebounce, useGetEnterprisesAutocomplete } from "@api";
import type { IContact } from "@bbj-types/contact";
import type { ObjectId } from "@bbj-types/utils";
import {
  Autocomplete,
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { type SyntheticEvent, useEffect } from "react";

export interface IEnterpriseAutocomplete {
  _id?: ObjectId;
  name: string;
  contacts?: IContact[];
}

interface IAutocompleteEnterprisesProps {
  value: IEnterpriseAutocomplete | null;
  onChange: (
    event: SyntheticEvent<Element, Event>,
    value: IEnterpriseAutocomplete | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<IEnterpriseAutocomplete> | undefined
  ) => void;
  onData?: (data: IEnterpriseAutocomplete[]) => void;
  onLoading?: (loading: boolean) => void;
  TextFieldProps?: TextFieldProps;
  disableAddEnterprise?: boolean;
}

const filter = createFilterOptions<IEnterpriseAutocomplete>();

export const AutocompleteEnterprises = ({
  value,
  onData,
  onLoading,
  TextFieldProps,
  disableAddEnterprise = false,
  ...props
}: IAutocompleteEnterprisesProps) => {
  const [{ data, loading }, getEnterprisesAutocomplete] = useGetEnterprisesAutocomplete();

  const handleEnterprisesAutocomplete = useDebounce(
    (_: SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        getEnterprisesAutocomplete({ search: value, limit: 10 });
      }
    },
    750,
    []
  );

  useEffect(() => {
    if (onData && data) {
      onData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (onLoading) {
      onLoading(loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <>
      <Autocomplete
        {...props}
        value={value}
        onInputChange={handleEnterprisesAutocomplete}
        options={data || []}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (
            !disableAddEnterprise &&
            params.inputValue !== "" &&
            !options.some((option) => option.name === params.inputValue)
          ) {
            filtered.push({
              name: params.inputValue,
            });
          }
          return filtered;
        }}
        loading={loading}
        renderOption={(props, option) =>
          option._id ? (
            <Typography component="li" {...props} variant="body1">
              {option.name}
            </Typography>
          ) : (
            <Stack
              component="li"
              sx={{
                mt: 1,
                gap: 1,
              }}
            >
              <Divider sx={{ mx: 2 }} />
              <Typography {...props} variant="body1SemiBold" color="primary" sx={{ alignSelf: "stretch" }}>
                + Renseigner une nouvelle entreprise
              </Typography>
            </Stack>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            {...TextFieldProps}
            size="small"
            slotProps={{

              input: {
                ...params.InputProps,
                ...TextFieldProps?.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }
            }}
          />
        )}
        fullWidth
      />
    </>
  );
};
